import React, { Fragment, useContext, useEffect, useState } from "react";
import { useSignIn, useSignOut } from "react-auth-kit";
import { useNavigate, useParams } from "react-router-dom";
import {
  getTokenFromCookie,
  sendApiRequest,
} from "../../services/Base/base-service";

import AnalysisPreview from "../../components/AnalysisPreview";
import SubNavigation from "../../components/Header/SubNavigation";
import {
  Alert,
  Input,
  InputGroup,
  NotificationManager,
  Section,
  Select,
  Spinner,
} from "design-react-kit";

import UserContext from "../../store/user-context";
import FileExistMessageModal from "../../components/Modals/FileExistMessageModal";
import secureLocalStorage from "react-secure-storage";

const Preview = () => {
  const [results, setResults] = useState(null);
  const [file, setFile] = useState(null);
  const [fileForModal, setFileForModal] = useState(null);
  const [analyzedFiles, setAnalyzedFiles] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setIsModalOpen] = useState(false);
  const [value, setValue] = useState(null);
  const [documentAnalysisResultId, setDocumentAnalysisResultId] =
    useState(null);

  const params = useParams();
  const clientId = params?.clientId;
  const documentTypeId = params?.documentTypeId;

  useEffect(() => {
    getAllAnalyzedFilesHandler(clientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userContext = useContext(UserContext);

  const signIn = useSignIn();
  const signOut = useSignOut();
  const navigate = useNavigate();

  const docPattern = secureLocalStorage.getItem("docPattern");

  const checkFileAnalysisPresenceHandler = (clientId, file) => {
    setIsLoading(true);
    sendApiRequest(
      `DocumentAnalysis/CheckFileAnalysisPresence?clientId=${clientId}&fileName=${encodeURIComponent(
        file.name
      )}&fileSize=${file.size}`,
      "GET",
      true,
      null,
      signIn,
      signOut,
      navigate,
      null,
      userContext
    ).then((response) => {
      if (response !== undefined) {
        if (response.errors !== null) {
          setError(response.errors);
          setIsLoading(false);
        } else {
          if (response.data.idDocumentAnalysisResult !== 0) {
            setIsModalOpen(true);
            setIsLoading(false);
            setDocumentAnalysisResultId(response.data.idDocumentAnalysisResult);
          } else {
            setTimeout(() => {
              uploadFileHandler(clientId, file);
              setIsLoading(false);
            }, 500);
          }
        }
      }
    });
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const uploadFileHandler = async (clientId, file) => {
    setIsLoading(true);
    const base64FileContent = await toBase64(file);
    const fileResult = base64FileContent.split(",")[1];
    let formData = new FormData();

    // Append the file to the FormData object
    formData.append("FileName", encodeURI(file.name));
    formData.append("FileSize", file.size);
    formData.append("FileContent", fileResult);
    formData.append("clientId", clientId);
    formData.append("documentType", documentTypeId);

    const token = getTokenFromCookie();

    // Create a new XMLHttpRequest object
    const xhr = new XMLHttpRequest();

    // Set up the request
    xhr.open(
      "POST",
      `${process.env.REACT_APP_API_URL}DocumentAnalysis/UploadDocument`,
      true
    );

    // Set the content type header
    xhr.setRequestHeader("Authorization", "Bearer " + token);

    // Handle the response
    xhr.onload = function () {
      if (xhr.status === 200) {
        runDocumentAnalysisHandler(xhr.response);
        setIsLoading(false);
      } else {
        // File upload failed
        setError([xhr.response]);
        setIsLoading(false);
      }
    };
    // Send the request
    xhr.send(formData);
  };

  const runDocumentAnalysisHandler = (analysisResultId) => {
    setIsLoading(true);
    sendApiRequest(
      `DocumentAnalysis/RunDocumentAnalysis`,
      "POST",
      true,
      {
        analysisResultId,
      },
      signIn,
      signOut,
      navigate,
      null,
      userContext
    ).then((resp) => {
      if (resp !== undefined) {
        if (resp.errors !== null) {
          setError(resp.errors);
          setIsLoading(false);
        } else {
          if (resp.data) {
            setTimeout(() => {
              getFileAnalysisResultHandler(analysisResultId);
              getAllAnalyzedFilesHandler(clientId);
              setIsLoading(false);
            }, 500);
          }
          setIsLoading(false);
        }
      }
    });
  };

  const getAllAnalyzedFilesHandler = (clientId) => {
    setIsLoading(true);
    sendApiRequest(
      `DocumentAnalysis/GetAllAnalyzedFiles?clientId=${clientId}&documentType=${documentTypeId}`,
      "GET",
      true,
      null,
      signIn,
      signOut,
      navigate,
      null,
      userContext
    ).then((response) => {
      if (response !== undefined) {
        if (response.errors !== null) {
          setError(response.errors);
          setIsLoading(false);
        } else {
          const files = [];
          response.data.awsFileAnalysisResults.forEach((file) => {
            files.push({ value: file.fileResultId, label: file.fileName });
          });
          setAnalyzedFiles(files);
          setIsLoading(false);
          if (response.data.awsFileAnalysisResults.length > 0) {
            setValue({
              value: response.data.awsFileAnalysisResults[0].fileResultId,
              label: response.data.awsFileAnalysisResults[0].fileName,
            });
            setTimeout(() => {
              getFileAnalysisResultHandler(
                response.data.awsFileAnalysisResults[0].fileResultId
              );
            }, 500);
          }
        }
      }
    });
  };

  const getFileAnalysisResultHandler = (fileResultId) => {
    setIsLoading(true);
    sendApiRequest(
      `DocumentAnalysis/GetFileAnalysisResult?fileResultId=${fileResultId}`,
      "GET",
      true,
      null,
      signIn,
      signOut,
      navigate,
      null,
      userContext
    ).then((response) => {
      if (response !== undefined) {
        if (response.errors !== null) {
          setError(response.errors);
          setIsLoading(false);
        } else {
          setResults({
            ...response.data,
            jsonKeyValuePairs:
              response.data.jsonKeyValuePairs !== ""
                ? JSON.parse(response.data.jsonKeyValuePairs)
                : "",
          });
          setFile(response.data.fileContent);
          setIsLoading(false);
        }
      }
    });
  };

  const handleChange = (selectedOption) => {
    getFileAnalysisResultHandler(selectedOption.value);
    setValue(selectedOption);
    setFileForModal(null);
  };

  const modalCloseHandler = () => setIsModalOpen(false);

  function matches(name, docPattern) {
    return (
      name.includes(docPattern.toLowerCase()) &&
      name.endsWith(".pdf") &&
      name.includes("#")
    );
  }

  return (
    <Fragment>
      <SubNavigation page="preview" />
      <Section>
        <h3 className="mb-5">
          Effettua l'upload del file che vuoi analizzare o selezionalo
          dall'elenco
        </h3>
        <div className="d-flex align-items-center justify-content-start mb-5">
          <div className="bootstrap-select-wrapper w-25 mr-3 mt-0">
            <label htmlFor="selectExampleClassic">
              Elenco files già analizzati:
            </label>
            <Select
              id="selectExampleClassic"
              onChange={handleChange}
              value={value}
              //Nessun file selezionato
              options={analyzedFiles}
              placeholder="Seleziona file"
              aria-label="Seleziona file"
              className="w-100"
            />
          </div>
          <InputGroup className="w-50 d-flex align-items-center">
            <form id="file-form">
              <label
                htmlFor="file"
                role="button"
                className="btn-secondary rounded p-2 mr-2"
              >
                Scegli file da caricare
              </label>
              <Input
                className="btn"
                id="file"
                type="file"
                style={{ display: "none" }}
                accept="application/pdf"
                onClick={(e) => document.getElementById("file-form").reset()}
                onChange={(e) => {
                  setError(null);

                  if (
                    e.target.files[0] !== undefined &&
                    e.target.files[0] !== null
                  ) {
                    const name = e.target.files[0].name.toLowerCase();

                    if (matches(name, docPattern)) {
                      checkFileAnalysisPresenceHandler(
                        clientId,
                        e.target.files[0]
                      );
                      setFileForModal(e.target.files[0]);
                    } else {
                      setError([
                        <p>
                          Il nome del file scelto non soddisfa le regole di
                          identificazione configurate (deve contenere il
                          carattere{" "}
                          <span className="font-weight-bold"> # </span> come
                          separatore dell'identificatore gruppo e deve contenere
                          <span className="font-weight-bold">
                            {" "}
                            {docPattern}{" "}
                          </span>{" "}
                          ) !
                        </p>,
                      ]);
                    }
                  }
                }}
              />
              <span>
                {fileForModal === null && "Nessun file scelto"}
                {fileForModal !== null &&
                  file !== undefined &&
                  `${fileForModal.name}`}
              </span>
            </form>
          </InputGroup>
        </div>
        {error !== null && error.length !== undefined && (
          <Alert color="danger" className="mx-auto">
            {error.map((item) => (
              <p key={item}>{item}</p>
            ))}
          </Alert>
        )}
        {isLoading && (
          <p
            style={{
              height: "50px",
              position: "relative",
              marginBottom: "3rem",
            }}
          >
            <Spinner double active />
          </p>
        )}
        {file !== null && results !== null && (
          <div className="content d-flex">
            <AnalysisPreview
              file={file}
              results={results}
              documentTypeId={documentTypeId}
            />
          </div>
        )}
        <div>
          <NotificationManager
            containerId="esempio-avanzato"
            fix="right"
            closeOnClick={true}
            state="success"
            duration={30000}
          />
        </div>
        <FileExistMessageModal
          modalOpen={modalOpen}
          modalCloseHandler={modalCloseHandler}
          uploadFileHandler={uploadFileHandler}
          getFileAnalysisResultHandler={getFileAnalysisResultHandler}
          data={{ clientId: clientId, file: fileForModal }}
          documentAnalysisResultId={documentAnalysisResultId}
        />
      </Section>
    </Fragment>
  );
};

export default Preview;
